import { useState, useRef, useEffect } from "react";
import { HiChevronDown, HiSearch } from "react-icons/hi";

interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  searchPlaceholder?: string;
  id: string;
  label: string;
}

export const MultiSelect = ({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder = "Suchen...",
  id,
  label,
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listboxId = `${id}-listbox`;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleSelectAll = () => {
    if (value.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((option) => option.value));
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, callback: () => void) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      callback();
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label
        htmlFor={id}
        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
      >
        {label}
      </label>
      <button
        type="button"
        id={id}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen ? "true" : "false"}
        aria-controls={listboxId}
        aria-haspopup="true"
        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-2 text-left text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      >
        <span className="block truncate">
          {value.length > 0 ? `${value.length} ausgewählt` : placeholder}
        </span>
        <HiChevronDown
          className={`h-5 w-5 transition-transform ${
            isOpen ? "rotate-180 transform" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700"
          id={listboxId}
          role="listbox"
          aria-label={label}
        >
          <div className="p-2">
            <div className="relative">
              <HiSearch
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="text"
                id={`${id}-search`}
                className="w-full rounded-md border border-gray-300 py-1 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-600 dark:text-white dark:placeholder-gray-400"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label={searchPlaceholder}
              />
            </div>
          </div>

          <div className="max-h-60 overflow-auto">
            <div className="border-t border-gray-200 py-1 dark:border-gray-600">
              <div
                role="option"
                aria-selected={value.length === options.length}
                className="flex cursor-pointer items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={handleSelectAll}
                onKeyDown={(e) => handleKeyDown(e, handleSelectAll)}
                tabIndex={0}
              >
                <input
                  type="checkbox"
                  id={`${id}-select-all`}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600"
                  checked={value.length === options.length}
                  readOnly
                  aria-label="Alle auswählen"
                />
                <span className="ml-3 text-sm text-gray-900 dark:text-white">
                  Alle
                </span>
              </div>
            </div>

            {filteredOptions.map((option) => (
              <div
                key={option.value}
                role="option"
                aria-selected={value.includes(option.value)}
                className="flex cursor-pointer items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => handleToggleOption(option.value)}
                onKeyDown={(e) =>
                  handleKeyDown(e, () => handleToggleOption(option.value))
                }
                tabIndex={0}
              >
                <input
                  type="checkbox"
                  id={`${id}-option-${option.value}`}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-500 dark:bg-gray-600"
                  checked={value.includes(option.value)}
                  readOnly
                  aria-label={option.label}
                />
                <span className="ml-3 text-sm text-gray-900 dark:text-white">
                  {option.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
